// Const
import { organizationType } from "const/nuclias/organization";
import { userType } from "const/nuclias/user";
import { metadataSetField } from "./const";

// Service
import AuthService from "services/Auth.service";
import AuthOrganizationService from "services/Auth.Organization.service";

// Model
import MetadataSetModel from "./models/MetadataSet";
import SamlRoleModel from "./models/SamlRole";

const getAuthResources = async () => {
  const response = AuthService.getAuthResources();
  return response;
}

const getSelectedOrg = (isOrgLevel, pageContext) => {
  let selectedOrg = null;

  if (!isOrgLevel) {
    selectedOrg = pageContext.sp || pageContext.si;
  } else {
    selectedOrg = pageContext.selectedOrg;
  }

  return selectedOrg;
}

const getMetaDataSet = async (orgId) => {
  const response = await AuthOrganizationService.getMetaDataSet(orgId);
  return response;
}

const getSamlRole = async (orgId) => {
  const response = await AuthOrganizationService.getSamlRoles(orgId);
  return response;
}

const checkSamlSsoState = (metadataSetList) => {
  return metadataSetList.some(metadataSet => metadataSet.enable);
}

const parseRawData = (modelName, rawDataList, orgTreeMap, selectedOrg) => {
  const modelNameList = {
    'MetadataSet': MetadataSetModel,
    'SamlRole': SamlRoleModel,
  };
  const list = [];


  for (const rawData of rawDataList) {
    list.push(new modelNameList[modelName](rawData, orgTreeMap, selectedOrg));
  }

  return list;
}

const changeSamlSsoState = async (orgId, metaDataSetList) => {
  const payload = {
    enableSettings: []
  };

  for (const metaDataSet of metaDataSetList) {
    payload.enableSettings.push({ setId: metaDataSet.id, enable: !metaDataSet.enable });
  }

  const response = await AuthOrganizationService.updateMetaDataSetStatus(orgId, payload);
  return response;
}

const addOrSaveMetadataSet = async (orgId, selectedMetadataSetId, metadataSetState) => {
  const payload = {};

  if (metadataSetState.byManualOrImport.value) {
    for (const key in metadataSetState) {
      const { type, apiKey, value, file } = metadataSetState[key];

      if (key === metadataSetField.byManualOrImport || !value) {
        continue;
      }

      if (type === String) {
        payload[apiKey] = value;
      }

      if (type === Object && key === metadataSetField.certificate) {
        const { idpCertFile, idpCertFileName } = apiKey;
        payload[idpCertFile] = file;
        payload[idpCertFileName] = value;
      }
    }
  } else {
    for (const key in metadataSetState) {
      const { type, apiKey, value, file } = metadataSetState[key];

      if (key === metadataSetField.byManualOrImport || !value) {
        continue;
      }

      if (type === String) {
        payload[apiKey] = value;
      }

      if (type === Object && key === metadataSetField.metadataSetXml) {
        const { idpMetaDataXmlFile, idpMetaDataXmlFileName } = apiKey;
        payload[idpMetaDataXmlFile] = file;
        payload[idpMetaDataXmlFileName] = value;
      }
    }
  }

  const response = await AuthOrganizationService.addOrUpdateMetadataSet(orgId, selectedMetadataSetId, payload);

  return response;
}

const addOrSaveSamlRole = async (orgId, editedSamlRoleId = null, samRole) => {
  const payload = {
    name: '',
    privilege: '',
    type: '',
    resources: [],
  };

  payload.type = samRole.accessPrivilegeLevelType.selected.value;
  payload.name = samRole.name.value;
  payload.privilege = samRole.accessPrivilegeRole.selected.value;

  // MSP
  if (payload.type === organizationType.si) {
    // manage MSP
    payload.resources.push({ id: samRole.accessPrivilegeLevelOrg.selected.id, type: organizationType.si });
  }

  // Organization
  if (payload.type !== organizationType.sp && payload.type !== organizationType.si) {
    if (samRole.managedSiteTag.selected.id === 0 && samRole.managedSite.selected.id === 0) {
      // manage Organization
      payload.type = userType.org;
      payload.resources.push({ id: samRole.managedOrganization.selected.id, type: organizationType.org });
    } else if (samRole.managedSiteTag.selected.id !== 0 && samRole.managedSite.selected.id === 0) {
      // manage Site tag
      payload.type = userType.siteTag;
      payload.resources.push({ id: samRole.managedSiteTag.selected.id, type: organizationType.tag });
    } else {
      // managee Site
      payload.type = userType.site;
      payload.resources.push({ id: samRole.managedSite.selected.id, type: organizationType.site });
    }
  }

  let response = null;
  if (!editedSamlRoleId) {
    response = await AuthOrganizationService.addSamlRole(orgId, payload);
  } else {
    response = await AuthOrganizationService.upadteSamlRole(orgId, editedSamlRoleId, payload);
  }

  return response;
}

const getCheckAllState = (sourceList) => {
  let checkedAll = true;

  if (sourceList.length === 0) {
    checkedAll = false;
  } else {
    for (const source of sourceList) {
      if (!source.checked) {
        checkedAll = false;
      }
    }
  }

  return checkedAll;
}

const toggleCheckedAll = (sourceList) => {
  let nowState = getCheckAllState(sourceList);

  const updatedSourceList = [...sourceList];
  for (const source of updatedSourceList) {
    source.checked = !nowState;
  }

  return updatedSourceList;
}

const toggleChecked = (sourceList, index) => {
  const updatedSourceList = [...sourceList];
  updatedSourceList[index].checked = !updatedSourceList[index].checked;
  return updatedSourceList;
}

const getDeleteButtonState = (sourceList) => {
  let canDelete = false;
  for (const source of sourceList) {
    if (source.checked) {
      canDelete = true;
    }
  }

  return canDelete;
}

const deleteMetadataSet = async (orgId, idList) => {
  const payload = { setIds: idList };
  const response = await AuthOrganizationService.deleteMetaDataSet(orgId, payload);

  return response;
}

const deleteSamlRole = async (orgId, idList) => {
  const payload = { roleIds: idList };
  const response = await AuthOrganizationService.deleteSamlRole(orgId, payload);

  return response;
}

export default {
  getSelectedOrg,
  getAuthResources,
  getMetaDataSet,
  getSamlRole,
  checkSamlSsoState,
  parseRawData,
  changeSamlSsoState,
  addOrSaveMetadataSet,
  addOrSaveSamlRole,
  getCheckAllState,
  toggleCheckedAll,
  toggleChecked,
  getDeleteButtonState,
  deleteMetadataSet,
  deleteSamlRole
}